import { useCallback, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { CHART_APPEARANCE_DEFAULTS } from "@/constants/widget-types";
import { Palette, Grid, BarChart3, LineChart, Eye } from "lucide-react";
import { IWidgetAppearance, VisualizationType } from "@/types/dashboard.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useGetFormFields } from "@/hooks";
import { useMultipleIndividualFormFields } from "@/hooks";

interface DynamicAppearanceEditorProps {
  appearance: IWidgetAppearance;
  onChange: (appearance: IWidgetAppearance) => void;
  visualizationType: VisualizationType;
  cctConfig?: {
    formId: string;
    factors: { fieldId: string; label?: string }[];
    measures: { id?: string; fieldId: string; aggregation: string; label?: string }[];
  };
}

interface VisibilityRules {
  [key: string]: VisualizationType[];
}

const VISIBILITY_RULES: VisibilityRules = {
  // Color Palettes - based on actual WidgetRenderer usage
  presetCategoricalPaletteId: ['bar', 'line', 'histogram', 'scatter'], // Pie uses auto colors
  presetSequentialPaletteId: ['calendar-heatmap', 'crosstab'], // Actually used for heatmap intensity and crosstab heatmap
  legend: ['bar', 'line', 'pie', 'histogram', 'scatter'],

  // Bar Chart Specific
  barOrientation: ['bar'],
  barCombinationMode: ['bar'],

  // Line Chart Specific
  lineStyle: ['line'],
  showPoints: ['line'],
  pointSize: ['line'],

  // Axes (for charts with coordinate systems)
  showXAxisLabels: ['bar', 'line', 'histogram', 'scatter'],
  showYAxisLabels: ['bar', 'line', 'histogram', 'scatter'],
  xAxisLabelRotation: ['bar', 'histogram'],

  // Grid (for charts with grid backgrounds)
  showGrid: ['bar', 'line', 'histogram', 'scatter'],
  gridStyle: ['bar', 'line', 'histogram', 'scatter'],
  gridColor: ['bar', 'line', 'histogram', 'scatter'],

  // Crosstab-specific
  showRowTotals: ['crosstab'],
  showColumnTotals: ['crosstab'],
  showGrandTotal: ['crosstab'],
  numberFormat: ['crosstab', 'cct'],
  crosstabColoringMode: ['crosstab', 'cct'],
  valueColor: ['crosstab', 'cct'],
  compactDensity: ['crosstab', 'cct'],
  cellBorder: ['crosstab', 'cct'],
  // CCT-specific
  cctColoringScope: ['cct'],
  // Compact number formatting
  compactXAxis: ['bar', 'line', 'histogram', 'scatter'],
  compactYAxis: ['bar', 'line', 'histogram', 'scatter'],
  compactCardValue: ['card'],
};

const PRESET_PALETTES = [
  { id: 'default', name: 'Default', colors: ['#8884d8', '#82ca9d', '#ffc658'] },
  { id: 'blues', name: 'Blues', colors: ['#1f77b4', '#aec7e8', '#c5dbf7'] },
  { id: 'greens', name: 'Greens', colors: ['#2ca02c', '#98df8a', '#c9f2c9'] },
  { id: 'reds', name: 'Reds', colors: ['#d62728', '#ff9896', '#ffcccb'] },
  { id: 'purples', name: 'Purples', colors: ['#9467bd', '#c5b0d5', '#e7d4f0'] },
  { id: 'oranges', name: 'Oranges', colors: ['#ff7f0e', '#ffbb78', '#ffd6b8'] },
  { id: 'teals', name: 'Teals', colors: ['#17becf', '#9edae5', '#c7f0f5'] },
  { id: 'earth', name: 'Earth Tones', colors: ['#8c564b', '#c49c94', '#e5d3ca'] },
];

export function DynamicAppearanceEditor({
  appearance,
  onChange,
  visualizationType,
  cctConfig
}: DynamicAppearanceEditorProps) {
  const { t } = useTranslation();
  const currentAppearance = useMemo(() => ({
    ...CHART_APPEARANCE_DEFAULTS,
    ...appearance,
  }), [appearance]);

  // Get form fields for CCT measures to display proper field names
  const cctFormIds = useMemo(() => {
    if (!cctConfig?.measures) return [];
    return Array.from(new Set(cctConfig.measures.map(m => cctConfig.formId).filter(Boolean)));
  }, [cctConfig?.measures, cctConfig?.formId]);

  const { formFieldsMap } = useMultipleIndividualFormFields(cctFormIds);

  // Helper function to get field name from fieldId
  const getFieldName = useCallback((fieldId: string, formId: string): string => {
    if (!fieldId || !formId) return fieldId;
    const formFields = formFieldsMap[formId] || [];
    const field = formFields.find(f => f.id === fieldId);
    return field?.label || fieldId;
  }, [formFieldsMap]);

  const isVisible = useCallback((setting: string): boolean => {
    const rule = VISIBILITY_RULES[setting];
    return rule ? rule.includes(visualizationType) : false;
  }, [visualizationType]);

  const updateAppearance = useCallback((updates: Partial<IWidgetAppearance>) => {
    onChange({
      ...currentAppearance,
      ...updates,
    });
  }, [currentAppearance, onChange]);

  const visibleSections = useMemo(() => {
    const sections: string[] = [];

    // Colors section: includes palettes and legend
    if (
      isVisible('presetCategoricalPaletteId') ||
      isVisible('presetSequentialPaletteId') ||
      isVisible('legend')
    ) {
      sections.push('colors');
    }

    // Chart-specific styling section
    if (
      isVisible('barOrientation') ||
      isVisible('barCombinationMode') ||
      isVisible('lineStyle') ||
      isVisible('showPoints')
    ) {
      sections.push('chart');
    }

    // Axes and grid section
    if (
      isVisible('showGrid') ||
      isVisible('showXAxisLabels') ||
      isVisible('showYAxisLabels')
    ) {
      sections.push('axes');
    }

    // Crosstab section - always show for crosstab type
    if (visualizationType === 'crosstab') {
      sections.push('crosstab');
    }

    // CCT section - always show for cct type
    if (visualizationType === 'cct') {
      sections.push('cct');
    }

    return sections;
  }, [isVisible]);

  if (visibleSections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            {t('processManagement.appearance')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Eye className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>{t('processManagement.noAppearanceSettingsAvailable', { type: visualizationType })}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="w-5 h-5" />
          {t('processManagement.appearanceSettings')}
        </CardTitle>
        <div className="flex gap-1 flex-wrap">
          {visibleSections.map(section => (
            <Badge key={section} variant="outline" className="text-xs">
              {section}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {visibleSections.includes('background') && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded border" />
              <Label className="text-sm font-medium">{t('processManagement.background')}</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="backgroundColor" className="text-sm">{t('processManagement.backgroundColor')}</Label>
              <div className="flex gap-2">
                <Input
                  id="backgroundColor"
                  type="color"
                  value={currentAppearance.backgroundColor || "#ffffff"}
                  onChange={(e) => updateAppearance({ backgroundColor: e.target.value })}
                  className="w-16 h-10 p-1 rounded cursor-pointer"
                />
                <Input
                  value={currentAppearance.backgroundColor || "#ffffff"}
                  onChange={(e) => updateAppearance({ backgroundColor: e.target.value })}
                  placeholder="#ffffff"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        )}

        {visibleSections.includes('colors') && (
          <>
            {visibleSections.includes('background') && <Separator />}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                <Label className="text-sm font-medium">{t('processManagement.colorsLegend')}</Label>
              </div>

              {isVisible('presetCategoricalPaletteId') && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('processManagement.colorPalette')}</Label>
                    <Select
                      value={currentAppearance.presetCategoricalPaletteId || 'default'}
                      onValueChange={(value) => updateAppearance({
                        presetCategoricalPaletteId: value,
                        paletteMode: 'preset'
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRESET_PALETTES.map((palette) => (
                          <SelectItem key={palette.id} value={palette.id}>
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">
                                {palette.colors.slice(0, 3).map((color, i) => (
                                  <div
                                    key={i}
                                    className="w-3 h-3 rounded-full border"
                                    style={{ backgroundColor: color }}
                                  />
                                ))}
                              </div>
                              <span>{palette.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {isVisible('presetSequentialPaletteId') && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('processManagement.heatmapColorIntensity')}</Label>
                    <Select
                      value={currentAppearance.presetSequentialPaletteId || 'blues'}
                      onValueChange={(value) => updateAppearance({
                        presetSequentialPaletteId: value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blues">{t('processManagement.blues')}</SelectItem>
                        <SelectItem value="greens">{t('processManagement.greens')}</SelectItem>
                        <SelectItem value="reds">{t('processManagement.reds')}</SelectItem>
                        <SelectItem value="purples">{t('processManagement.purples')}</SelectItem>
                        <SelectItem value="oranges">{t('processManagement.oranges')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {isVisible('legend') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.showLegend')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.displayChartLegendWhenMultipleSeries')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.legend ?? true}
                    onCheckedChange={(checked) => updateAppearance({ legend: checked })}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {visibleSections.includes('chart') && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {visualizationType === 'bar' ? <BarChart3 className="w-4 h-4" /> : <LineChart className="w-4 h-4" />}
                <Label className="text-sm font-medium">{t('processManagement.chartStyle')}</Label>
              </div>

              {isVisible('barOrientation') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.barOrientation')}</Label>
                  <Select
                    value={currentAppearance.barOrientation || 'vertical'}
                    onValueChange={(value: 'vertical' | 'horizontal') =>
                      updateAppearance({ barOrientation: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical">{t('processManagement.vertical')}</SelectItem>
                      <SelectItem value="horizontal">{t('processManagement.horizontal')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isVisible('barCombinationMode') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.barCombination')}</Label>
                  <Select
                    value={currentAppearance.barCombinationMode || 'grouped'}
                    onValueChange={(value: 'grouped' | 'stacked') =>
                      updateAppearance({ barCombinationMode: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grouped">{t('processManagement.groupedSideBySide')}</SelectItem>
                      <SelectItem value="stacked">{t('processManagement.stacked')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isVisible('lineStyle') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.lineStyle')}</Label>
                  <Select
                    value={currentAppearance.lineStyle || 'solid'}
                    onValueChange={(value: 'solid' | 'dashed' | 'dotted') =>
                      updateAppearance({ lineStyle: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">{t('processManagement.solid')}</SelectItem>
                      <SelectItem value="dashed">{t('processManagement.dashed')}</SelectItem>
                      <SelectItem value="dotted">{t('processManagement.dotted')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isVisible('showPoints') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.showDataPoints')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.displayDotsOnLineChart')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.showPoints ?? true}
                    onCheckedChange={(checked) => updateAppearance({ showPoints: checked })}
                  />
                </div>
              )}

              {isVisible('pointSize') && currentAppearance.showPoints && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('processManagement.pointSize')}</Label>
                    <span className="text-xs text-gray-500">{currentAppearance.pointSize || 3}px</span>
                  </div>
                  <Slider
                    value={[currentAppearance.pointSize || 3]}
                    onValueChange={([value]) => updateAppearance({ pointSize: value })}
                    max={8}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                </div>
              )}

              {isVisible('compactCardValue') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">Compact Value Display</Label>
                    <p className="text-xs text-gray-500">Display card value in abbreviated format (1000 → 1k)</p>
                  </div>
                  <Switch
                    checked={currentAppearance.compactCardValue ?? false}
                    onCheckedChange={(checked) => updateAppearance({ compactCardValue: checked })}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {visibleSections.includes('cct') && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                <Label className="text-sm font-medium">{t('processManagement.cctOptions')}</Label>
              </div>

              {/* Coloring Scope */}
              {isVisible('cctColoringScope') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.coloringScope')}</Label>
                  <Select
                    value={currentAppearance.cctColoringScope || 'global'}
                    onValueChange={(value: 'global' | 'per-measure') =>
                      updateAppearance({ cctColoringScope: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">{t('processManagement.global')}</SelectItem>
                      <SelectItem value="per-measure">{t('processManagement.perMeasure')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {currentAppearance.cctColoringScope === 'global'
                      ? t('processManagement.globalColoringDescription')
                      : t('processManagement.perMeasureColoringDescription')
                    }
                  </p>
                </div>
              )}

              {/* Per-measure coloring configuration */}
              {currentAppearance.cctColoringScope === 'per-measure' && cctConfig?.measures && cctConfig.measures.length > 0 && (
                <div className="space-y-4">
                  <Label className="text-sm font-medium">{t('processManagement.perMeasureColoring')}</Label>
                  {cctConfig.measures.map((measure, index) => (
                    <div key={measure.id || index} className="p-3 border rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {measure.label || `${getFieldName(measure.fieldId, cctConfig.formId)} (${measure.aggregation})`}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {measure.aggregation}
                        </Badge>
                      </div>

                      {/* Coloring mode for this measure */}
                      <div className="space-y-2">
                        <Label className="text-sm">{t('processManagement.cellColoring')}</Label>
                        <Select
                          value={currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.mode || 'solid'}
                          onValueChange={(value: 'solid' | 'heatmap' | 'conditions') => {
                            const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                            const updatedMeasureColoring = {
                              ...currentMeasureColoring,
                              [`${measure.fieldId}_${measure.aggregation}`]: {
                                ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                mode: value
                              }
                            };
                            updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="solid">{t('processManagement.solidColor')}</SelectItem>
                            <SelectItem value="heatmap">{t('processManagement.heatmap')}</SelectItem>
                            <SelectItem value="conditions">{t('processManagement.conditionBased')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Solid color for this measure */}
                      {currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.mode === 'solid' && (
                        <div className="space-y-2">
                          <Label className="text-sm">{t('processManagement.cellColor')}</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.color || "#000000"}
                              onChange={(e) => {
                                const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                const updatedMeasureColoring = {
                                  ...currentMeasureColoring,
                                  [`${measure.fieldId}_${measure.aggregation}`]: {
                                    ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                    color: e.target.value
                                  }
                                };
                                updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                              }}
                              className="w-16 h-10 p-1 rounded cursor-pointer"
                            />
                            <Input
                              value={currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.color || "#000000"}
                              onChange={(e) => {
                                const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                const updatedMeasureColoring = {
                                  ...currentMeasureColoring,
                                  [`${measure.fieldId}_${measure.aggregation}`]: {
                                    ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                    color: e.target.value
                                  }
                                };
                                updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                              }}
                              placeholder="#000000"
                              className="flex-1"
                            />
                          </div>
                        </div>
                      )}

                      {/* Heatmap palette for this measure */}
                      {currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.mode === 'heatmap' && (
                        <div className="space-y-2">
                          <Label className="text-sm">{t('processManagement.heatmapColorIntensity')}</Label>
                          <Select
                            value={currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.palette || 'blues'}
                            onValueChange={(value) => {
                              const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                              const updatedMeasureColoring = {
                                ...currentMeasureColoring,
                                [`${measure.fieldId}_${measure.aggregation}`]: {
                                  ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                  palette: value
                                }
                              };
                              updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="blues">{t('processManagement.blues')}</SelectItem>
                              <SelectItem value="greens">{t('processManagement.greens')}</SelectItem>
                              <SelectItem value="reds">{t('processManagement.reds')}</SelectItem>
                              <SelectItem value="purples">{t('processManagement.purples')}</SelectItem>
                              <SelectItem value="oranges">{t('processManagement.oranges')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Conditions for this measure */}
                      {currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.mode === 'conditions' && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm">{t('processManagement.conditions')}</Label>
                            <p className="text-xs text-gray-500">{t('forms.firstMatchingConditionApplies')}</p>
                            <div className="space-y-2">
                              {(currentAppearance.cctMeasureColoring?.[`${measure.fieldId}_${measure.aggregation}`]?.conditions || []).map((condition, conditionIndex) => (
                                <div key={conditionIndex} className="flex items-center gap-2 p-2 border rounded">
                                  <Select
                                    value={condition.operator}
                                    onValueChange={(operator: any) => {
                                      const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                      const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                      const updatedConditions = [...currentConditions];
                                      updatedConditions[conditionIndex] = { ...condition, operator };
                                      const updatedMeasureColoring = {
                                        ...currentMeasureColoring,
                                        [`${measure.fieldId}_${measure.aggregation}`]: {
                                          ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                          conditions: updatedConditions
                                        }
                                      };
                                      updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                    }}
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="equals">{t('processManagement.equals')}</SelectItem>
                                      <SelectItem value="not_equals">{t('processManagement.notEquals')}</SelectItem>
                                      <SelectItem value="greater_than">{t('processManagement.greaterThan')}</SelectItem>
                                      <SelectItem value="greater_or_equal">{t('processManagement.greaterOrEqual')}</SelectItem>
                                      <SelectItem value="less_than">{t('processManagement.lessThan')}</SelectItem>
                                      <SelectItem value="less_or_equal">{t('processManagement.lessOrEqual')}</SelectItem>
                                      <SelectItem value="between">{t('processManagement.between')}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    type="number"
                                    value={condition.value}
                                    onChange={(e) => {
                                      const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                      const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                      const updatedConditions = [...currentConditions];
                                      updatedConditions[conditionIndex] = { ...condition, value: Number(e.target.value) };
                                      const updatedMeasureColoring = {
                                        ...currentMeasureColoring,
                                        [`${measure.fieldId}_${measure.aggregation}`]: {
                                          ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                          conditions: updatedConditions
                                        }
                                      };
                                      updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                    }}
                                    className="w-20"
                                    placeholder={t('processManagement.value')}
                                  />
                                  {condition.operator === 'between' && (
                                    <Input
                                      type="number"
                                      value={condition.value2 || ''}
                                      onChange={(e) => {
                                        const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                        const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                        const updatedConditions = [...currentConditions];
                                        updatedConditions[conditionIndex] = { ...condition, value2: Number(e.target.value) };
                                        const updatedMeasureColoring = {
                                          ...currentMeasureColoring,
                                          [`${measure.fieldId}_${measure.aggregation}`]: {
                                            ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                            conditions: updatedConditions
                                          }
                                        };
                                        updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                      }}
                                      className="w-20"
                                      placeholder={t('processManagement.valueTo')}
                                    />
                                  )}
                                  <Input
                                    type="color"
                                    value={condition.color}
                                    onChange={(e) => {
                                      const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                      const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                      const updatedConditions = [...currentConditions];
                                      updatedConditions[conditionIndex] = { ...condition, color: e.target.value };
                                      const updatedMeasureColoring = {
                                        ...currentMeasureColoring,
                                        [`${measure.fieldId}_${measure.aggregation}`]: {
                                          ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                          conditions: updatedConditions
                                        }
                                      };
                                      updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                    }}
                                    className="w-12 h-8 p-1 rounded cursor-pointer"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                      const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                      const updatedConditions = currentConditions.filter((_, i) => i !== conditionIndex);
                                      const updatedMeasureColoring = {
                                        ...currentMeasureColoring,
                                        [`${measure.fieldId}_${measure.aggregation}`]: {
                                          ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                          conditions: updatedConditions
                                        }
                                      };
                                      updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const currentMeasureColoring = currentAppearance.cctMeasureColoring || {};
                                  const currentConditions = currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`]?.conditions || [];
                                  const newCondition = {
                                    operator: 'greater_than' as const,
                                    value: 0,
                                    color: '#000000'
                                  };
                                  const updatedMeasureColoring = {
                                    ...currentMeasureColoring,
                                    [`${measure.fieldId}_${measure.aggregation}`]: {
                                      ...currentMeasureColoring[`${measure.fieldId}_${measure.aggregation}`],
                                      conditions: [...currentConditions, newCondition]
                                    }
                                  };
                                  updateAppearance({ cctMeasureColoring: updatedMeasureColoring });
                                }}
                              >
                                {t('processManagement.addCondition')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Global coloring options - only show when scope is global */}
              {currentAppearance.cctColoringScope !== 'per-measure' && (
                <>
                  {/* Reuse crosstab appearance options */}
                  {isVisible('numberFormat') && (
                    <div className="space-y-2">
                      <Label className="text-sm">{t('processManagement.numberFormat')}</Label>
                      <Select
                        value={currentAppearance.numberFormat || 'raw'}
                        onValueChange={(value: 'raw' | 'rowPct' | 'colPct' | 'grandPct') =>
                          updateAppearance({ numberFormat: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="raw">{t('processManagement.rawNumbers')}</SelectItem>
                          <SelectItem value="rowPct">{t('processManagement.percentageOfRow')}</SelectItem>
                          <SelectItem value="colPct">{t('processManagement.percentageOfColumn')}</SelectItem>
                          <SelectItem value="grandPct">{t('processManagement.percentageOfGrandTotal')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Cell Coloring */}
                  {isVisible('crosstabColoringMode') && (
                    <div className="space-y-2">
                      <Label className="text-sm">{t('processManagement.cellColoring')}</Label>
                      <Select
                        value={currentAppearance.crosstabColoringMode || 'solid'}
                        onValueChange={(value: 'solid' | 'heatmap' | 'conditions') =>
                          updateAppearance({ crosstabColoringMode: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="solid">{t('processManagement.solidColor')}</SelectItem>
                          <SelectItem value="heatmap">{t('processManagement.heatmap')}</SelectItem>
                          <SelectItem value="conditions">{t('processManagement.conditionBased')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Solid Color */}
                  {isVisible('valueColor') && currentAppearance.crosstabColoringMode === 'solid' && (
                    <div className="space-y-2">
                      <Label className="text-sm">{t('processManagement.cellColor')}</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={currentAppearance.valueColor || "#000000"}
                          onChange={(e) => updateAppearance({ valueColor: e.target.value })}
                          className="w-16 h-10 p-1 rounded cursor-pointer"
                        />
                        <Input
                          value={currentAppearance.valueColor || "#000000"}
                          onChange={(e) => updateAppearance({ valueColor: e.target.value })}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  )}

                  {/* Heatmap Palette */}
                  {isVisible('presetSequentialPaletteId') && currentAppearance.crosstabColoringMode === 'heatmap' && (
                    <div className="space-y-2">
                      <Label className="text-sm">{t('processManagement.heatmapColorIntensity')}</Label>
                      <Select
                        value={currentAppearance.presetSequentialPaletteId || 'blues'}
                        onValueChange={(value) => updateAppearance({
                          presetSequentialPaletteId: value
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blues">{t('processManagement.blues')}</SelectItem>
                          <SelectItem value="greens">{t('processManagement.greens')}</SelectItem>
                          <SelectItem value="reds">{t('processManagement.reds')}</SelectItem>
                          <SelectItem value="purples">{t('processManagement.purples')}</SelectItem>
                          <SelectItem value="oranges">{t('processManagement.oranges')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Global conditions */}
                  {currentAppearance.crosstabColoringMode === 'conditions' && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm">{t('processManagement.conditions')}</Label>
                        <p className="text-xs text-gray-500">{t('forms.firstMatchingConditionApplies')}</p>
                        <div className="space-y-2">
                          {(currentAppearance.crosstabConditions || []).map((condition, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 border rounded">
                              <Select
                                value={condition.operator}
                                onValueChange={(operator: any) => {
                                  const newConditions = [...(currentAppearance.crosstabConditions || [])];
                                  newConditions[index] = { ...condition, operator };
                                  updateAppearance({ crosstabConditions: newConditions });
                                }}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="equals">{t('processManagement.equals')}</SelectItem>
                                  <SelectItem value="not_equals">{t('processManagement.notEquals')}</SelectItem>
                                  <SelectItem value="greater_than">{t('processManagement.greaterThan')}</SelectItem>
                                  <SelectItem value="greater_or_equal">{t('processManagement.greaterOrEqual')}</SelectItem>
                                  <SelectItem value="less_than">{t('processManagement.lessThan')}</SelectItem>
                                  <SelectItem value="less_or_equal">{t('processManagement.lessOrEqual')}</SelectItem>
                                  <SelectItem value="between">{t('processManagement.between')}</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                type="number"
                                value={condition.value}
                                onChange={(e) => {
                                  const newConditions = [...(currentAppearance.crosstabConditions || [])];
                                  newConditions[index] = { ...condition, value: Number(e.target.value) };
                                  updateAppearance({ crosstabConditions: newConditions });
                                }}
                                className="w-20"
                                placeholder={t('processManagement.value')}
                              />
                              {condition.operator === 'between' && (
                                <Input
                                  type="number"
                                  value={condition.value2 || ''}
                                  onChange={(e) => {
                                    const newConditions = [...(currentAppearance.crosstabConditions || [])];
                                    newConditions[index] = { ...condition, value2: Number(e.target.value) };
                                    updateAppearance({ crosstabConditions: newConditions });
                                  }}
                                  className="w-20"
                                  placeholder={t('processManagement.valueTo')}
                                />
                              )}
                              <Input
                                type="color"
                                value={condition.color}
                                onChange={(e) => {
                                  const newConditions = [...(currentAppearance.crosstabConditions || [])];
                                  newConditions[index] = { ...condition, color: e.target.value };
                                  updateAppearance({ crosstabConditions: newConditions });
                                }}
                                className="w-12 h-8 p-1 rounded cursor-pointer"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newConditions = (currentAppearance.crosstabConditions || []).filter((_, i) => i !== index);
                                  updateAppearance({ crosstabConditions: newConditions });
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newCondition = {
                                operator: 'greater_than' as const,
                                value: 0,
                                color: '#000000'
                              };
                              const newConditions = [...(currentAppearance.crosstabConditions || []), newCondition];
                              updateAppearance({ crosstabConditions: newConditions });
                            }}
                          >
                            {t('processManagement.addCondition')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Layout Options */}
              {isVisible('compactDensity') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.compactDensity')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.reduceCellPadding')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.compactDensity ?? false}
                    onCheckedChange={(checked) => updateAppearance({ compactDensity: checked })}
                  />
                </div>
              )}

              {isVisible('cellBorder') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.cellBorders')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.showBordersAroundCells')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.cellBorder ?? true}
                    onCheckedChange={(checked) => updateAppearance({ cellBorder: checked })}
                  />
                </div>
              )}

            </div>
          </>
        )}

        {visibleSections.includes('axes') && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                <Label className="text-sm font-medium">{t('processManagement.axesGrid')}</Label>
              </div>

              {isVisible('showGrid') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.showGridLines')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.displayBackgroundGridLines')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.showGrid ?? true}
                    onCheckedChange={(checked) => updateAppearance({ showGrid: checked })}
                  />
                </div>
              )}

              {/* {isVisible('gridStyle') && currentAppearance.showGrid && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('forms.gridStyle')}</Label>
                  <Select
                    value={currentAppearance.gridStyle || 'solid'}
                    onValueChange={(value: 'solid' | 'dashed' | 'dotted') =>
                      updateAppearance({ gridStyle: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">{t('processManagement.solid')}</SelectItem>
                      <SelectItem value="dashed">{t('processManagement.dashed')}</SelectItem>
                      <SelectItem value="dotted">{t('processManagement.dotted')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )} */}

              {isVisible('showXAxisLabels') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.showXAxisLabels')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.displayLabelsOnHorizontalAxis')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.showXAxisLabels ?? true}
                    onCheckedChange={(checked) => updateAppearance({ showXAxisLabels: checked })}
                  />
                </div>
              )}

              {isVisible('xAxisLabelRotation') && currentAppearance.showXAxisLabels && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">{t('processManagement.xAxisLabelRotation')}</Label>
                    <span className="text-xs text-gray-500">{currentAppearance.xAxisLabelRotation || 0}°</span>
                  </div>
                  <Slider
                    value={[currentAppearance.xAxisLabelRotation || 0]}
                    onValueChange={([value]) => updateAppearance({ xAxisLabelRotation: value })}
                    max={90}
                    min={0}
                    step={15}
                    className="w-full"
                  />
                </div>
              )}

              {isVisible('showYAxisLabels') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.showYAxisLabels')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.displayLabelsOnVerticalAxis')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.showYAxisLabels ?? true}
                    onCheckedChange={(checked) => updateAppearance({ showYAxisLabels: checked })}
                  />
                </div>
              )}

              {isVisible('compactXAxis') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">Compact X-Axis Numbers</Label>
                    <p className="text-xs text-gray-500">Display X-axis numbers in abbreviated format (1000 → 1k)</p>
                  </div>
                  <Switch
                    checked={currentAppearance.compactXAxis ?? false}
                    onCheckedChange={(checked) => updateAppearance({ compactXAxis: checked })}
                  />
                </div>
              )}

              {isVisible('compactYAxis') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">Compact Y-Axis Numbers</Label>
                    <p className="text-xs text-gray-500">Display Y-axis numbers in abbreviated format (1000 → 1k)</p>
                  </div>
                  <Switch
                    checked={currentAppearance.compactYAxis ?? false}
                    onCheckedChange={(checked) => updateAppearance({ compactYAxis: checked })}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {visibleSections.includes('crosstab') && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                <Label className="text-sm font-medium">{t('processManagement.crosstabOptions')}</Label>
              </div>

              {/* Totals */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{t('processManagement.totals')}</Label>

                {isVisible('showRowTotals') && (
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('processManagement.showRowTotals')}</Label>
                      <p className="text-xs text-gray-500">{t('processManagement.displayTotalColumnForEachRow')}</p>
                    </div>
                    <Switch
                      checked={currentAppearance.showRowTotals ?? true}
                      onCheckedChange={(checked) => updateAppearance({ showRowTotals: checked })}
                    />
                  </div>
                )}

                {isVisible('showColumnTotals') && (
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('processManagement.showColumnTotals')}</Label>
                      <p className="text-xs text-gray-500">{t('processManagement.displayTotalRowForEachColumn')}</p>
                    </div>
                    <Switch
                      checked={currentAppearance.showColumnTotals ?? true}
                      onCheckedChange={(checked) => updateAppearance({ showColumnTotals: checked })}
                    />
                  </div>
                )}

                {isVisible('showGrandTotal') && (
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="text-sm">{t('processManagement.showGrandTotal')}</Label>
                      <p className="text-xs text-gray-500">{t('processManagement.displayGrandTotalInBottomRight')}</p>
                    </div>
                    <Switch
                      checked={currentAppearance.showGrandTotal ?? true}
                      onCheckedChange={(checked) => updateAppearance({ showGrandTotal: checked })}
                    />
                  </div>
                )}
              </div>

              {/* Number Format */}
              {isVisible('numberFormat') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.numberFormat')}</Label>
                  <Select
                    value={currentAppearance.numberFormat || 'raw'}
                    onValueChange={(value: 'raw' | 'rowPct' | 'colPct' | 'grandPct') =>
                      updateAppearance({ numberFormat: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="raw">{t('processManagement.rawNumbers')}</SelectItem>
                      <SelectItem value="rowPct">{t('processManagement.percentageOfRow')}</SelectItem>
                      <SelectItem value="colPct">{t('processManagement.percentageOfColumn')}</SelectItem>
                      <SelectItem value="grandPct">{t('processManagement.percentageOfGrandTotal')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Cell Coloring */}
              {isVisible('crosstabColoringMode') && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.cellColoring')}</Label>
                  <Select
                    value={currentAppearance.crosstabColoringMode || 'solid'}
                    onValueChange={(value: 'solid' | 'heatmap' | 'conditions') =>
                      updateAppearance({ crosstabColoringMode: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solid">{t('processManagement.solidColor')}</SelectItem>
                      <SelectItem value="heatmap">{t('processManagement.heatmap')}</SelectItem>
                      <SelectItem value="conditions">{t('processManagement.conditionBased')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Solid Color */}
              {isVisible('valueColor') && currentAppearance.crosstabColoringMode === 'solid' && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.cellColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={currentAppearance.valueColor || "#000000"}
                      onChange={(e) => updateAppearance({ valueColor: e.target.value })}
                      className="w-16 h-10 p-1 rounded cursor-pointer"
                    />
                    <Input
                      value={currentAppearance.valueColor || "#000000"}
                      onChange={(e) => updateAppearance({ valueColor: e.target.value })}
                      placeholder="#000000"
                      className="flex-1"
                    />
                  </div>
                </div>
              )}

              {/* Heatmap Palette */}
              {isVisible('presetSequentialPaletteId') && currentAppearance.crosstabColoringMode === 'heatmap' && (
                <div className="space-y-2">
                  <Label className="text-sm">{t('processManagement.heatmapColorIntensity')}</Label>
                  <Select
                    value={currentAppearance.presetSequentialPaletteId || 'blues'}
                    onValueChange={(value) => updateAppearance({
                      presetSequentialPaletteId: value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blues">{t('processManagement.blues')}</SelectItem>
                      <SelectItem value="greens">{t('processManagement.greens')}</SelectItem>
                      <SelectItem value="reds">{t('processManagement.reds')}</SelectItem>
                      <SelectItem value="purples">{t('processManagement.purples')}</SelectItem>
                      <SelectItem value="oranges">{t('processManagement.oranges')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Condition-based coloring */}
              {currentAppearance.crosstabColoringMode === 'conditions' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm">{t('processManagement.conditions')}</Label>
                    <p className="text-xs text-gray-500">First matching condition applies</p>
                    <div className="space-y-2">
                      {(currentAppearance.crosstabConditions || []).map((condition, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 border rounded">
                          <Select
                            value={condition.operator}
                            onValueChange={(operator: any) => {
                              const newConditions = [...(currentAppearance.crosstabConditions || [])];
                              newConditions[index] = { ...condition, operator };
                              updateAppearance({ crosstabConditions: newConditions });
                            }}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">{t('processManagement.equals')}</SelectItem>
                              <SelectItem value="not_equals">{t('processManagement.notEquals')}</SelectItem>
                              <SelectItem value="greater_than">{t('processManagement.greaterThan')}</SelectItem>
                              <SelectItem value="greater_or_equal">{t('processManagement.greaterOrEqual')}</SelectItem>
                              <SelectItem value="less_than">{t('processManagement.lessThan')}</SelectItem>
                              <SelectItem value="less_or_equal">{t('processManagement.lessOrEqual')}</SelectItem>
                              <SelectItem value="between">{t('processManagement.between')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={condition.value}
                            onChange={(e) => {
                              const newConditions = [...(currentAppearance.crosstabConditions || [])];
                              newConditions[index] = { ...condition, value: Number(e.target.value) };
                              updateAppearance({ crosstabConditions: newConditions });
                            }}
                            className="w-20"
                            placeholder={t('processManagement.value')}
                          />
                          {condition.operator === 'between' && (
                            <Input
                              type="number"
                              value={condition.value2 || ''}
                              onChange={(e) => {
                                const newConditions = [...(currentAppearance.crosstabConditions || [])];
                                newConditions[index] = { ...condition, value2: Number(e.target.value) };
                                updateAppearance({ crosstabConditions: newConditions });
                              }}
                              className="w-20"
                              placeholder={t('processManagement.valueTo')}
                            />
                          )}
                          <Input
                            type="color"
                            value={condition.color}
                            onChange={(e) => {
                              const newConditions = [...(currentAppearance.crosstabConditions || [])];
                              newConditions[index] = { ...condition, color: e.target.value };
                              updateAppearance({ crosstabConditions: newConditions });
                            }}
                            className="w-12 h-8 p-1 rounded cursor-pointer"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newConditions = (currentAppearance.crosstabConditions || []).filter((_, i) => i !== index);
                              updateAppearance({ crosstabConditions: newConditions });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newCondition = {
                            operator: 'greater_than' as const,
                            value: 0,
                            color: '#000000'
                          };
                          const newConditions = [...(currentAppearance.crosstabConditions || []), newCondition];
                          updateAppearance({ crosstabConditions: newConditions });
                        }}
                      >
                        {t('processManagement.addCondition')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Layout Options */}
              {isVisible('compactDensity') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.compactDensity')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.reduceCellPadding')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.compactDensity ?? false}
                    onCheckedChange={(checked) => updateAppearance({ compactDensity: checked })}
                  />
                </div>
              )}

              {isVisible('cellBorder') && (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('processManagement.cellBorders')}</Label>
                    <p className="text-xs text-gray-500">{t('processManagement.showBordersAroundCells')}</p>
                  </div>
                  <Switch
                    checked={currentAppearance.cellBorder ?? true}
                    onCheckedChange={(checked) => updateAppearance({ cellBorder: checked })}
                  />
                </div>
              )}

            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
